import React, { Component } from "react";
import * as Business from "../../utils/business";
import Loading from "../../components/Loading";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Treemap,
} from "recharts";

const C_YELLOW = "#FFE600";
const C_NAVY = "#2E2E38";
const C_GRAY = "#74747A";
const C_LIGHT = "#C4C4CD";
const C_TEAL = "#168736";
const C_DARK2 = "#1A2E4A";

// ─── Mock Data ──────────────────────────────────────────────────────────────

const RWA_BY_RATING = [
  { label: "AAA to AA-", pct: 8.8, color: C_TEAL },
  { label: "A+ to A-", pct: 36.3, color: C_DARK2 },
  { label: "BBB+ to BBB-", pct: 3.3, color: C_GRAY },
  { label: "BB+ to B-", pct: 45.4, color: C_YELLOW },
  { label: "Below B- or unrated", pct: 6.3, color: "#C0392B" },
];

const TOP_10_CUSTOMERS = [
  { name: "Vingroup", rwa: 18450, pct: 5.3 },
  { name: "Masan", rwa: 15230, pct: 4.4 },
  { name: "VinFast", rwa: 12800, pct: 3.7 },
  { name: "Sun Group", rwa: 11600, pct: 3.3 },
  { name: "FPT Corp", rwa: 9870, pct: 2.8 },
  { name: "Hoa Phat", rwa: 8540, pct: 2.5 },
  { name: "VNPT", rwa: 7320, pct: 2.1 },
  { name: "EVN", rwa: 6890, pct: 2.0 },
  { name: "PVN", rwa: 6200, pct: 1.8 },
  { name: "VNA", rwa: 5780, pct: 1.7 },
];

const TOP_10_BRANCHES = [
  { name: "HCM City", rwa: 45200, pct: 13.0 },
  { name: "Hanoi HQ", rwa: 38600, pct: 11.1 },
  { name: "Da Nang", rwa: 12400, pct: 3.6 },
  { name: "Hai Phong", rwa: 10300, pct: 3.0 },
  { name: "Can Tho", rwa: 8700, pct: 2.5 },
  { name: "Binh Duong", rwa: 7800, pct: 2.2 },
  { name: "Dong Nai", rwa: 7200, pct: 2.1 },
  { name: "Quang Ninh", rwa: 6100, pct: 1.8 },
  { name: "Nghe An", rwa: 5400, pct: 1.6 },
  { name: "BRVT", rwa: 4900, pct: 1.4 },
];

const TOP_5_SECTORS = [
  { name: "Real Estate", rwa: 95800, pct: 27.5 },
  { name: "Manufacturing", rwa: 82400, pct: 23.7 },
  { name: "Trade & Retail", rwa: 48600, pct: 14.0 },
  { name: "Construction", rwa: 38200, pct: 11.0 },
  { name: "Agriculture", rwa: 28700, pct: 8.2 },
];

const RWA_BY_SECTOR = [
  { name: "Real Estate", size: 95800, fill: C_YELLOW },
  { name: "Manufacturing", size: 82400, fill: C_DARK2 },
  { name: "Trade & Retail", size: 48600, fill: C_NAVY },
  { name: "Construction", size: 38200, fill: C_GRAY },
  { name: "Agriculture", size: 28700, fill: "#8AAD3B" },
  { name: "Financial Svcs", size: 22300, fill: C_TEAL },
  { name: "Transportation", size: 18400, fill: "#9B6B35" },
  { name: "Energy", size: 15600, fill: "#6B3598" },
  { name: "Services", size: 12300, fill: C_LIGHT },
  { name: "Healthcare", size: 6128, fill: "#C0392B" },
];

const RWA_BY_DEPT = [
  { name: "Treasury Department", value: 36.86 },
  { name: "Corporate Department", value: 31.37 },
  { name: "Retail Department", value: 25.91 },
  { name: "Other", value: 5.86 },
];
const DEPT_COLORS = [C_NAVY, C_DARK2, C_YELLOW, C_TEAL];

const RWA_BY_ASSET = [
  { name: "Specialized lending", value: 53.83 },
  { name: "Corp, sovereign & bank", value: 27.69 },
  { name: "Qualifying revolving retail", value: 14.18 },
  { name: "Residential mortgage", value: 3.49 },
  { name: "Other retail", value: 0.82 },
];
const ASSET_COLORS = [C_NAVY, C_DARK2, C_YELLOW, C_GRAY, C_LIGHT];

// ─── Shared Styles ───────────────────────────────────────────────────────────

const panelStyle = {
  background: "#fff",
  borderRadius: 6,
  padding: "14px 14px 10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

const panelTitle = {
  fontWeight: "600",
  color: C_NAVY,
  fontSize: 12,
  marginBottom: 8,
  paddingLeft: 8,
  borderLeft: `3px solid ${C_YELLOW}`,
};

// ─── Sub-components ──────────────────────────────────────────────────────────

class RatingRing extends Component {
  render() {
    const { label, pct, color } = this.props;
    const r = 38;
    const cx = 50;
    const cy = 50;
    const circumference = 2 * Math.PI * r;
    const filled = (pct / 100) * circumference;
    return (
      <div style={{ textAlign: "center", flex: 1 }}>
        <svg width={100} height={100} viewBox="0 0 100 100">
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#e8e8e8"
            strokeWidth={9}
          />
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={9}
            strokeDasharray={`${filled} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          <text
            x={cx}
            y={cy + 1}
            textAnchor="middle"
            dominantBaseline="central"
            fill={C_NAVY}
            fontSize={15}
            fontWeight="bold"
          >
            {pct}%
          </text>
        </svg>
        <div
          style={{
            fontSize: 11,
            color: C_NAVY,
            marginTop: 4,
            lineHeight: 1.35,
            padding: "0 4px",
          }}
        >
          {label}
        </div>
      </div>
    );
  }
}

class SectorTreemapContent extends Component {
  render() {
    const { x, y, width, height, name, fill, size } = this.props;
    const bg = fill || C_GRAY;
    const textColor = bg === C_YELLOW ? C_NAVY : "#fff";
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={bg}
          stroke="#fff"
          strokeWidth={1.5}
          style={{ cursor: "pointer" }}
        />
        {width > 50 && height > 28 && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (height > 55 ? 10 : 0)}
            textAnchor="middle"
            dominantBaseline="central"
            fill={textColor}
            fontSize={Math.min(12, Math.max(8, width / 7))}
            fontWeight={width > 90 ? "bold" : "normal"}
          >
            {name}
          </text>
        )}
        {width > 70 && height > 50 && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 12}
            textAnchor="middle"
            dominantBaseline="central"
            fill={`${textColor}CC`}
            fontSize={Math.min(10, Math.max(8, width / 9))}
          >
            {(size || 0).toLocaleString()}
          </text>
        )}
      </g>
    );
  }
}

const ComboTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: "8px 12px",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: "bold", color: C_NAVY, marginBottom: 4 }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || C_GRAY, marginBottom: 2 }}>
          {p.name}:{" "}
          <strong>
            {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
            {p.name === "%" ? "%" : ""}
          </strong>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: "8px 12px",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: "bold", color: C_NAVY, marginBottom: 2 }}>
        {item.name}
      </div>
      <div style={{ color: C_GRAY }}>{Number(item.value).toFixed(2)}%</div>
    </div>
  );
};

const SectorTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: "8px 12px",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: "bold", color: C_NAVY, marginBottom: 2 }}>
        {item.name}
      </div>
      <div style={{ color: C_GRAY }}>
        {(item.size || 0).toLocaleString()} VND Billion
      </div>
    </div>
  );
};

const renderPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10}
      fontWeight="bold"
    >
      {`${Number(value).toFixed(1)}%`}
    </text>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default class CreditRisk extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      date: moment().local().endOf("day"),
      isShowDashboard: false,
    };
  }

  componentDidMount() {
    this.handleEffectShowDashboard();
  }

  handleEffectShowDashboard() {
    this.setState({ loading: true }, () => {
      setTimeout(
        () => this.setState({ loading: false, isShowDashboard: true }),
        500,
      );
    });
  }

  renderComboChart(title, data, barColor) {
    return (
      <div style={Object.assign({}, panelStyle, { flex: 1 })}>
        <div style={panelTitle}>{title}</div>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart
            data={data}
            margin={{ top: 5, right: 30, left: 0, bottom: 40 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9, fill: C_NAVY }}
              angle={-40}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{ fontSize: 9, fill: C_GRAY }}
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
              width={36}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 9, fill: C_GRAY }}
              tickFormatter={(v) => `${v}%`}
              width={32}
            />
            <Tooltip content={<ComboTooltip />} />
            <Bar
              yAxisId="left"
              dataKey="rwa"
              name="RWA (B.VND)"
              fill={barColor}
              radius={[3, 3, 0, 0]}
            />
            <Line
              yAxisId="right"
              dataKey="pct"
              name="%"
              stroke={C_NAVY}
              strokeWidth={2}
              dot={{ fill: C_NAVY, r: 3 }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  renderDashboard() {
    if (this.state.loading) {
      return (
        <div style={{ height: Business.getHeightContent() }}>
          <Loading size="small" />
        </div>
      );
    }
    if (!this.state.isShowDashboard) {
      return <div style={{ minHeight: 500 }} />;
    }

    return (
      <div style={{ marginTop: 14 }}>
        {/* Row 1: RWA by Rating rings */}
        <div style={Object.assign({}, panelStyle, { marginBottom: 12 })}>
          <div style={panelTitle}>RWA By Rating</div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "flex-start",
              padding: "8px 0 4px",
            }}
          >
            {RWA_BY_RATING.map((r, i) => (
              <RatingRing key={i} label={r.label} pct={r.pct} color={r.color} />
            ))}
          </div>
        </div>

        {/* Row 2: Combo charts */}
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          {this.renderComboChart(
            "Top 10 Customers by Risk-Weighted Asset",
            TOP_10_CUSTOMERS,
            C_YELLOW,
          )}
          {this.renderComboChart(
            "Top 10 Branches by Risk-Weighted Asset",
            TOP_10_BRANCHES,
            "#74C2E1",
          )}
          {this.renderComboChart(
            "Top 5 Sectors by Risk-Weighted Asset",
            TOP_5_SECTORS,
            "#8AAD3B",
          )}
        </div>

        {/* Row 3: Treemap + 2 Donuts */}
        <div style={{ display: "flex", gap: 12 }}>
          {/* RWA by Sector Treemap */}
          <div style={Object.assign({}, panelStyle, { flex: 1.4 })}>
            <div style={panelTitle}>RWA By Sector</div>
            <ResponsiveContainer width="100%" height={280}>
              <Treemap
                data={RWA_BY_SECTOR}
                dataKey="size"
                stroke="#fff"
                content={<SectorTreemapContent />}
              >
                <Tooltip content={<SectorTooltip />} />
              </Treemap>
            </ResponsiveContainer>
          </div>

          {/* RWA by Department Donut */}
          <div style={Object.assign({}, panelStyle, { flex: 1 })}>
            <div style={panelTitle}>RWA by Department</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={RWA_BY_DEPT}
                  cx="50%"
                  cy="46%"
                  innerRadius={55}
                  outerRadius={90}
                  dataKey="value"
                  labelLine={false}
                  label={renderPieLabel}
                  isAnimationActive={true}
                >
                  {RWA_BY_DEPT.map((e, i) => (
                    <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(v) => (
                    <span style={{ fontSize: 10, color: C_NAVY }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* RWA by Asset Class Donut */}
          <div style={Object.assign({}, panelStyle, { flex: 1 })}>
            <div style={panelTitle}>RWA By Asset Class</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={RWA_BY_ASSET}
                  cx="50%"
                  cy="46%"
                  innerRadius={55}
                  outerRadius={90}
                  dataKey="value"
                  labelLine={false}
                  label={renderPieLabel}
                  isAnimationActive={true}
                >
                  {RWA_BY_ASSET.map((e, i) => (
                    <Cell
                      key={i}
                      fill={ASSET_COLORS[i % ASSET_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(v) => (
                    <span style={{ fontSize: 10, color: C_NAVY }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div
        className="card"
        style={{ border: "none", backgroundColor: "#F2F2F2" }}
      >
        <div className="header" />
        <div className="content table-responsive table-full-width">
          <div className="row">
            <div className="col-md-3">
              <div style={{ marginRight: 10, display: "inline" }}>
                <label>Date</label>
              </div>
              <SingleDatePicker
                date={this.state.date}
                onDateChange={(date) =>
                  this.setState({ date }, () =>
                    this.handleEffectShowDashboard(),
                  )
                }
                focused={this.state.focused}
                onFocusChange={({ focused }) => this.setState({ focused })}
                showDefaultInputIcon
                isOutsideRange={() => false}
                inputIconPosition="after"
              />
            </div>
            <div className="col-md-6" />
            <div className="col-md-3" style={{ paddingTop: 10 }}>
              <label>Currency unit: Billion VND</label>
            </div>
          </div>
          {this.renderDashboard()}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// src/pages/Dashboard/CreditRisk.js